import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { companies } from "custom";
import { CustomApp } from "custom/types";

interface Context {
  customApp: Nullable<CustomApp>;
  language: Nullable<Pick<CustomApp, "event">>;
  loading: boolean;
}

const CustomAppContext = createContext<Nullable<Context>>(null);

interface Props {
  children: React.ReactNode;
}

export const CustomAppProvider: React.FC<Props> = ({ children }) => {
  const [customApp, setCustomApp] = useState<Context["customApp"]>(null);
  const [language, setLanguage] = useState<Context["language"]>(null);
  const [loading, setLoading] = useState(true);

  const value = useMemo(() => ({ customApp, loading, language }), [loading]);

  useEffect(() => {
    const host = window.location.host;

    console.log(`Checking config for host: ${host}`);

    const [, config] = companies.find(([re]) => !!host.match(re)) || [];

    if (config) {
      console.log("Found config:", config);
      setCustomApp(config);
    }
    setLanguage({
      event: {
        type: {
          singular: config?.event.type.singular || "Show",
          plural: config?.event.type.plural || "Shows",
          singularWithArticle:
            config?.event.type.singularWithArticle || "a Show",
        },
        owner: {
          singular: config?.event.owner.singular || "Artist",
          plural: config?.event.owner.plural || "Artists",
          singularWithArticle:
            config?.event.owner.singularWithArticle || "an Artist",
        },
        audience: {
          singular: config?.event.audience.singular || "Fan",
          plural: config?.event.audience.plural || "Fans",
          singularWithArticle:
            config?.event.audience.singularWithArticle || "a Fan",
        },
      },
    });
    setLoading(false);
  }, []);

  return (
    <CustomAppContext.Provider value={value}>
      {children}
    </CustomAppContext.Provider>
  );
};

export const useCustomAppContext = (): Context => {
  const context = useContext(CustomAppContext);

  if (context === undefined) {
    throw new Error(
      "useCustomAppContext must be used within a CustomAppProvider"
    );
  }

  return context!;
};
